"use client";

import { useTranslations, useLocale } from "use-intl";

import {
  CassetteIcon,
  CompactDiscIcon,
  MicrophoneIcon,
  MoreIcon,
  MusicIcon,
  MusicLibraryIcon,
  PersonLocationIcon,
  ReleaseFormatIcon,
  ReleaseIcon,
  VideoIcon,
} from "@/components/appIcons";
import type { AppIcon } from "@/components/appIcons";
import { formatCatalogTaxonomyLabel } from "@/lib/music-taxonomy";

import { FilterChip, CountryChipIcon, countryChipLabel } from "./filter-chip";
import { SearchOrganizationFilterChips } from "./search-organization-filter-chips";
import { SearchYearFilterChip } from "./search-year-filter-chip";
import type { SearchFilters } from "./use-search-state";

const FORMAT_ICONS: Record<string, AppIcon> = {
  vinyl: ReleaseFormatIcon,
  cd: CompactDiscIcon,
  cassette: CassetteIcon,
  video: VideoIcon,
};

const TYPE_ICONS: Record<string, AppIcon> = {
  album: ReleaseIcon,
  single_ep: MusicIcon,
  compilation: MusicLibraryIcon,
  live: MicrophoneIcon,
  video: VideoIcon,
  other: MoreIcon,
};

const FORMAT_LABEL_KEYS: Record<string, string> = {
  vinyl: "formatVinyl",
  cd: "formatCD",
  cassette: "formatCassette",
  video: "formatVideo",
};

export function SearchMusicFilterChips({
  filters,
  updateFilter,
  updateFilters,
  setFilter,
}: {
  filters: SearchFilters;
  updateFilter: (key: string, value: string | number | boolean | undefined) => void;
  updateFilters: (updates: Record<string, string | number | undefined>) => void;
  setFilter: (key: "label" | "company", value: string[] | undefined) => void;
}) {
  const t = useTranslations("globals.search");
  const tt = useTranslations("globals.search.albumType");
  const tg = useTranslations("shared.genres");
  const ts = useTranslations("shared.styles");
  const locale = useLocale();
  const genreKey = filters.genre as Parameters<typeof tg>[0];
  const styleKey = filters.style as Parameters<typeof ts>[0];
  return (
    <>
      {filters.genre && (
        <FilterChip icon={MusicIcon} onClear={() => updateFilter("genre", undefined)}>
          {tg.has(genreKey) ? tg(genreKey) : formatCatalogTaxonomyLabel(filters.genre)}
        </FilterChip>
      )}
      {filters.style && (
        <FilterChip icon={MusicIcon} onClear={() => updateFilter("style", undefined)}>
          {ts.has(styleKey) ? ts(styleKey) : formatCatalogTaxonomyLabel(filters.style)}
        </FilterChip>
      )}
      {filters.type && (
        <FilterChip
          icon={TYPE_ICONS[filters.type] ?? ReleaseIcon}
          onClear={() => updateFilter("type", undefined)}
        >
          {tt(filters.type)}
        </FilterChip>
      )}
      {filters.format && (
        <FilterChip
          icon={FORMAT_ICONS[filters.format] ?? ReleaseFormatIcon}
          onClear={() => updateFilter("format", undefined)}
        >
          {t(FORMAT_LABEL_KEYS[filters.format] as "formatVinyl")}
        </FilterChip>
      )}
      {filters.origin && (
        <FilterChip
          iconSlot={<CountryChipIcon code={filters.origin} fallbackIcon={PersonLocationIcon} />}
          onClear={() => updateFilter("origin", undefined)}
        >
          {countryChipLabel(filters.origin, locale)}
        </FilterChip>
      )}
      <SearchOrganizationFilterChips
        labels={filters.label}
        companies={filters.company}
        setFilter={setFilter}
      />
      <SearchYearFilterChip
        yearFrom={filters.yearFrom}
        yearTo={filters.yearTo}
        clear={() => updateFilters({ yearFrom: undefined, yearTo: undefined })}
      />
    </>
  );
}
